import { Component, type ReactNode, type ErrorInfo } from "react";
import { SolidAlert, UiReset } from "@/components/icons/solid-icons";
import { captureError } from "@/lib/sentry";
import "@/components/gates-native.css";

// A stale-chunk error means the service worker is serving an older
// bundle than the index.html on disk. window.location.reload() alone
// is not enough — the SW intercepts the reload and returns the same
// cached chunks, putting the user in a loop. To escape:
//   1. Unregister every active service worker for this origin.
//   2. Delete every CacheStorage cache (workbox precache + runtime).
//   3. Force-reload from the server (cache: 'reload').
// This is safe to do on every "stuck reload" because the SW will
// auto-register again on the next page load from the fresh bundle.
async function hardRecoverFromStaleBundle(): Promise<void> {
  try {
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
    }
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
    }
  } finally {
    window.location.reload();
  }
}

// A stale chunk after a deploy is not an error the user should ever see — it
// only means the tab was open across a release. We always recover silently
// (clear SW + caches + reload). To avoid a tight reload loop while a CDN is
// still serving the old index, each attempt within a short window waits a
// little longer before reloading (0s, 3s, 8s, 15s…); the user only ever sees
// a small "Updating…" spinner, never a prompt.
const STALE_RECOVER_KEY = "ch-stale-recover";
const STALE_RECOVER_WINDOW_MS = 120_000;
function nextRecoverDelay(): number {
  try {
    const raw = JSON.parse(sessionStorage.getItem(STALE_RECOVER_KEY) ?? "null") as { ts: number; n: number } | null;
    const n = raw && Date.now() - raw.ts < STALE_RECOVER_WINDOW_MS ? raw.n + 1 : 0;
    sessionStorage.setItem(STALE_RECOVER_KEY, JSON.stringify({ ts: Date.now(), n }));
    return [0, 3_000, 8_000, 15_000][Math.min(n, 3)]!;
  } catch {
    return 3_000;
  }
}

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  /** True while recovering from a stale chunk (silent reload in flight). */
  recovering: boolean;
}

// Stale-chunk errors fire when a new build has been deployed but the user
// still has the old index.html in memory, so a lazy route import goes to
// a chunk filename that no longer exists. Message wording differs by
// browser; match the union of patterns we've seen in the wild. These are
// NOT real bugs — the only fix is `window.location.reload()`, and they
// shouldn't pollute Sentry on every deploy.
function isStaleChunkError(err: Error | null): boolean {
  if (!err) return false;
  const msg = `${err.name ?? ""} ${err.message ?? ""}`.toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("importing a module script failed") ||
    // Older Chrome — surfaces as plain ChunkLoadError from webpack-style
    // splits; vite uses dynamic import but some libs still throw this.
    msg.includes("chunkloaderror") ||
    msg.includes("loading chunk") && msg.includes("failed")
  );
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null, recovering: false };

  static getDerivedStateFromError(error: Error): State {
    // A stale chunk that we haven't already tried to auto-recover from this
    // session → recover silently (spinner, not the scary prompt).
    return { hasError: true, error, recovering: isStaleChunkError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Stale-chunk errors are a deploy artifact (a lazy route chunk hash no
    // longer exists because a release shipped while this tab was open), not a
    // bug — never send them to Sentry.
    if (isStaleChunkError(error)) {
      const delay = nextRecoverDelay();
      console.warn(`ErrorBoundary: stale chunk after deploy — reloading${delay ? ` in ${delay / 1000}s` : ""}.`, error.message);
      window.setTimeout(() => void hardRecoverFromStaleBundle(), delay);
      return;
    }
    console.error("ErrorBoundary caught:", error, info);
    captureError(error, info);
  }

  render() {
    if (this.state.hasError) {
      const isDev = import.meta.env.DEV;
      // The module-kit card (styles/kit-chrome.css; native fit in
      // components/gates-native.css).
      if (this.state.recovering) {
        return (
          <div className="kc-updating" role="status" aria-live="polite">
            <UiReset size={22} className="animate-spin" />
            <p>Updating CampusHub…</p>
          </div>
        );
      }
      return (
        <div className="kc-error">
          <div className="kc-card sd-tone-red" role="alert">
            <span className="kc-icon kc-icon--lg sd-htone-red" aria-hidden>
              <SolidAlert size={22} />
            </span>
            <h2 className="kc-title">Something went wrong</h2>
            <p className="kc-error__msg">{this.state.error?.message ?? "An unexpected error occurred."}</p>
            {isDev && this.state.error?.stack && (
              <details className="kc-error__trace">
                <summary>Stack trace (dev only)</summary>
                <pre>{this.state.error.stack}</pre>
              </details>
            )}
            <div className="kc-error__actions">
              <button type="button" className="sd-btn sd-btn--primary" onClick={() => this.setState({ hasError: false, error: null })}>
                <UiReset size={14} />
                Try again
              </button>
              <button type="button" className="sd-btn" onClick={() => window.location.reload()}>
                Reload page
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
